import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type {
  CsvAnalyzeResponse,
  CsvParseResponse,
  LicenceResponse,
  ListOrigin,
  ListResponse,
  ListStatus,
  ProfileResponse,
} from '@shared/schemas';

import type { FilterCombinator, FilterGroup, FilterRule } from '@/types/filters';

import { EMPTY_ENRICHMENT_STATS, type EnrichmentStats } from '../../_shared/domain/enrichment';
import { toggleOverwriteRow } from '../../_shared/domain/file-import';
import {
  applyAnalyzeAcknowledged,
  applyAnalyzeFailed,
  applyAnalyzeStarted,
  applyAnalyzeSucceeded,
  applyMappingChanged,
  applyParseFailed,
  applyParseStarted,
  applyParseSucceeded,
  applyStepBack,
  type FileImportData,
} from '../../_shared/domain/file-import.model';
import {
  addGroup,
  addRule,
  clearTree,
  loadTree,
  moveNode,
  removeNode,
  setCombinator,
  updateRule,
} from '../../_shared/domain/filter-tree';
import {
  applyImportCleared,
  applyImportedProfileReplaced,
  applyImportExclusionToggled,
  applyImportPreviewLoaded,
  applyImportPreviewLoading,
  applyImportPreviewPageChanged,
  applyImportPreviewPageSizeChanged,
  applyImportSucceeded,
} from '../../_shared/domain/list-import.model';
import { applyMethodsChanged } from '../../_shared/domain/method-scope';
import type { SourceListOrigin } from '../../_shared/infrastructure/list-import-query';
import type { EditListError, ListMethod } from '../domain/edit-draft.model';
import { initialState } from './edit-list.state';

/** The text fields the details form edits directly (status/methods have their own actions). */
type EditTextField = 'name' | 'startDate' | 'endDate' | 'scope' | 'clientScopeDescription';

/** Payload for `loadSucceeded`: the metadata fields hydrated from the loaded entity. */
export interface LoadedList {
  listId: string;
  licenceId: string;
  licenceName: string;
  name: string;
  startDate: string;
  endDate: string;
  scope: string;
  clientScopeDescription: string;
  origin: ListOrigin;
  status: ListStatus;
}

/**
 * Edit List slice. Reducers are pure state transitions only; every side effect
 * (the initial load, validation trigger, API calls, delete) lives in the saga,
 * which dispatches the `load*` / `save*` / `delete*` transitions as it progresses.
 */
const slice = createSlice({
  name: 'editList',
  initialState,
  reducers: {
    // ── Load lifecycle (edit-only, dispatched by the saga) ──
    loadStarted: (state) => {
      state.loadStatus = 'loading';
    },
    loadSucceeded: (state, action: PayloadAction<LoadedList>) => {
      state.draft = { ...state.draft, ...action.payload };
      state.loadedName = action.payload.name;
      state.loadedLicenceId = action.payload.licenceId;
      state.loadedLicenceName = action.payload.licenceName;
      state.loadStatus = 'loaded';
    },
    loadFailed: (state) => {
      state.loadStatus = 'failed';
    },

    // ── Draft edits ──
    /** A single text field changed; clears that field's validation error. */
    fieldChanged: (state, action: PayloadAction<{ field: EditTextField; value: string }>) => {
      const { field, value } = action.payload;
      state.draft[field] = value;
      delete state.validationErrors[field];
    },
    statusChanged: (state, action: PayloadAction<ListStatus>) => {
      state.draft.status = action.payload;
    },
    /**
     * The licence transfer (#520), dispatched only once the admin has confirmed
     * the impact dialog; the UI intercepts the select's change before this.
     */
    licenceChanged: (state, action: PayloadAction<{ id: string; name: string }>) => {
      state.draft.licenceId = action.payload.id;
      state.draft.licenceName = action.payload.name;
    },
    /**
     * Change the selected methods AND drop what the unselected ones owned: an
     * uploaded file, imported ids, a filter tree. Leaving them behind is how a
     * previous step silently populated the saved list (docs/product-issues.md
     * PI-40); the UI confirms the loss before dispatching this.
     */
    methodsChanged: (state, action: PayloadAction<ListMethod[]>) => {
      applyMethodsChanged(state, state.draft.methods, action.payload);
      state.draft.methods = action.payload;
    },
    stepChanged: (state, action: PayloadAction<number>) => {
      state.step = action.payload;
    },

    // ── Save lifecycle (dispatched by the saga) ──
    saveStarted: (state) => {
      state.saveStatus = 'saving';
      state.error = null;
      state.validationErrors = {};
    },
    savePhaseSet: (state, action: PayloadAction<string>) => {
      state.savePhase = action.payload;
    },
    saveSucceeded: (state) => {
      state.saveStatus = 'succeeded';
      state.savePhase = '';
    },
    saveFailed: (state, action: PayloadAction<EditListError>) => {
      state.saveStatus = 'failed';
      state.error = action.payload;
      state.savePhase = '';
      if (action.payload.kind === 'validation') {
        state.validationErrors = action.payload.fields;
      }
    },

    // ── Delete lifecycle (edit-only) ──
    deleteStarted: (state) => {
      state.deleting = true;
      state.error = null;
    },
    deleteSucceeded: (state) => {
      state.deleting = false;
      state.deleted = true;
    },
    deleteFailed: (state, action: PayloadAction<EditListError>) => {
      state.deleting = false;
      state.error = action.payload;
    },

    // ── Selection ──
    excludeDuplicatesToggled: (state) => {
      state.excludeDuplicates = !state.excludeDuplicates;
    },
    importSet: (state, action: PayloadAction<{ ids: string[]; listName: string }>) => {
      state.importedProfileIds = action.payload.ids;
      state.importedListName = action.payload.listName;
    },
    importCleared: (state) => {
      applyImportCleared(state);
    },

    // ── "Import from existing list" step (shared transitions with Add) ──
    sourceOriginChanged: (state, action: PayloadAction<SourceListOrigin>) => {
      state.listImport.origin = action.payload;
    },
    sourceLicenceChanged: (state, action: PayloadAction<string>) => {
      state.listImport.sourceLicenceId = action.payload;
    },
    sourceListsLoadStarted: (state) => {
      state.listImport.sourceListsLoading = true;
    },
    /** Also the failure transition; a failed load lands an empty dropdown. */
    sourceListsLoaded: (state, action: PayloadAction<ListResponse[]>) => {
      state.listImport.sourceLists = action.payload;
      state.listImport.sourceListsLoading = false;
    },
    listImportStarted: (state) => {
      state.listImport.importing = true;
    },
    listImportSucceeded: (
      state,
      action: PayloadAction<{ rawIds: string[]; duplicateIds: string[]; listName: string }>,
    ) => {
      applyImportSucceeded(state, action.payload);
    },
    /** Silent failure; the button just re-enables, as the old step did. */
    listImportFailed: (state) => {
      state.listImport.importing = false;
    },
    importExcludeDuplicatesToggled: (state) => {
      applyImportExclusionToggled(state);
    },
    /** An imported profile was merged into another from the drawer (#746). */
    importedProfileReplaced: (state, action: PayloadAction<{ fromId: string; toId: string }>) => {
      applyImportedProfileReplaced(state, action.payload);
    },

    // ── Imported-profiles preview table (driven by the fetch saga) ──
    importPreviewLoading: (state) => {
      applyImportPreviewLoading(state.listImport);
    },
    importPreviewLoaded: (
      state,
      action: PayloadAction<{ rows: ProfileResponse[]; total: number }>,
    ) => {
      applyImportPreviewLoaded(state.listImport, action.payload);
    },
    importPreviewPageChanged: (state, action: PayloadAction<number>) => {
      applyImportPreviewPageChanged(state.listImport, action.payload);
    },
    importPreviewPageSizeChanged: (state, action: PayloadAction<number>) => {
      applyImportPreviewPageSizeChanged(state.listImport, action.payload);
    },

    // ── Licence dropdown of the import step ──
    licencesLoadStarted: (state) => {
      state.licencesLoading = true;
    },
    /** Also the failure transition; a failed load lands an empty dropdown. */
    licencesLoaded: (state, action: PayloadAction<LicenceResponse[]>) => {
      state.licences = action.payload;
      state.licencesLoading = false;
    },

    fileImportSet: (state, action: PayloadAction<FileImportData | null>) => {
      state.fileImportData = action.payload;
    },

    // ── Embedded file-import flow (delegates to the pure _shared transitions) ──
    fileParseStarted: (state, action: PayloadAction<{ fileName: string }>) => {
      state.fileUpload = applyParseStarted(state.fileUpload, action.payload.fileName);
      state.error = null;
    },
    fileParseSucceeded: (state, action: PayloadAction<CsvParseResponse>) => {
      state.fileUpload = applyParseSucceeded(state.fileUpload, action.payload);
    },
    fileParseFailed: (state, action: PayloadAction<EditListError>) => {
      state.fileUpload = applyParseFailed(state.fileUpload);
      state.error = action.payload;
    },
    fileMappingChanged: (state, action: PayloadAction<{ header: string; field: string }>) => {
      state.fileUpload = applyMappingChanged(
        state.fileUpload,
        action.payload.header,
        action.payload.field,
      );
    },
    fileAnalyzeStarted: (state) => {
      state.fileUpload = applyAnalyzeStarted(state.fileUpload);
      state.error = null;
    },
    /** Composes the deferred-import payload, including the CURRENT enriched
     * toggle, so later toggling stays consistent (see fileEnrichedToggled). */
    fileAnalyzeSucceeded: (state, action: PayloadAction<CsvAnalyzeResponse>) => {
      state.fileImportData = {
        columnMapping: state.fileUpload.columnMapping,
        rows: state.fileUpload.parseResult?.rows ?? [],
        analyzeResult: action.payload,
        fileName: state.fileUpload.fileName ?? '',
        isEnriched: state.fileImportData?.isEnriched ?? false,
        // A new analyze lists new conflicts: the previous choices no longer apply.
        overwriteRows: [],
      };
      state.fileUpload = applyAnalyzeSucceeded(state.fileUpload);
    },
    fileAnalyzeFailed: (state, action: PayloadAction<EditListError>) => {
      state.fileUpload = applyAnalyzeFailed(state.fileUpload);
      state.error = action.payload;
    },
    /** The step UI acknowledges the success signal before advancing the wizard. */
    fileAnalyzeAcknowledged: (state) => {
      state.fileUpload = applyAnalyzeAcknowledged(state.fileUpload);
    },
    fileStepBack: (state) => {
      state.fileUpload = applyStepBack(state.fileUpload);
    },
    /** Updates the STORED payload (the old stepper only updated local state,
     * so a toggle after analyze was silently lost; fixed by design here). */
    fileEnrichedToggled: (state, action: PayloadAction<boolean>) => {
      if (state.fileImportData) state.fileImportData.isEnriched = action.payload;
    },
    /** One exit conflict's overwrite, on or off (#709). */
    fileOverwriteRowToggled: (state, action: PayloadAction<number>) => {
      if (state.fileImportData) {
        state.fileImportData.overwriteRows = toggleOverwriteRow(
          state.fileImportData.overwriteRows,
          action.payload,
        );
      }
    },
    enrichNewToggled: (state) => {
      state.enrichNew = !state.enrichNew;
    },
    enrichStaleToggled: (state) => {
      state.enrichStale = !state.enrichStale;
    },

    // ── Filters (delegate to the pure filter-tree domain) ──
    ruleAdded: (state, action: PayloadAction<string | undefined>) => {
      state.filters = addRule(state.filters, action.payload);
    },
    groupAdded: (
      state,
      action: PayloadAction<{ parentId?: string; combinator?: FilterCombinator } | undefined>,
    ) => {
      state.filters = addGroup(state.filters, action.payload?.parentId, action.payload?.combinator);
    },
    nodeRemoved: (state, action: PayloadAction<string>) => {
      state.filters = removeNode(state.filters, action.payload);
    },
    ruleUpdated: (
      state,
      action: PayloadAction<{ id: string; patch: Partial<Omit<FilterRule, 'id' | 'kind'>> }>,
    ) => {
      state.filters = updateRule(state.filters, action.payload.id, action.payload.patch);
    },
    combinatorSet: (
      state,
      action: PayloadAction<{ groupId: string; combinator: FilterCombinator }>,
    ) => {
      state.filters = setCombinator(
        state.filters,
        action.payload.groupId,
        action.payload.combinator,
      );
    },
    nodeMoved: (
      state,
      action: PayloadAction<{ id: string; targetParentId: string; targetIndex: number }>,
    ) => {
      const { id, targetParentId, targetIndex } = action.payload;
      state.filters = moveNode(state.filters, id, targetParentId, targetIndex);
    },
    filtersCleared: (state) => {
      state.filters = clearTree(state.filters);
    },
    filtersLoaded: (state, action: PayloadAction<FilterGroup>) => {
      state.filters = loadTree(state.filters, action.payload);
    },

    // ── Profile table (driven by the fetch saga) ──
    pageChanged: (state, action: PayloadAction<number>) => {
      state.profiles.page = action.payload;
    },
    pageSizeChanged: (state, action: PayloadAction<number>) => {
      state.profiles.pageSize = action.payload;
      state.profiles.page = 0;
    },
    profilesLoading: (state) => {
      state.profiles.loading = true;
    },
    profilesLoaded: (state, action: PayloadAction<{ rows: ProfileResponse[]; total: number }>) => {
      state.profiles.rows = action.payload.rows;
      state.profiles.total = action.payload.total;
      state.profiles.loading = false;
    },

    // ── Async results (driven by their workers) ──
    duplicateLoadingStarted: (state) => {
      state.duplicateLoading = true;
    },
    duplicateCountSet: (state, action: PayloadAction<number>) => {
      state.duplicateCount = action.payload;
      state.duplicateLoading = false;
    },
    bucketsLoading: (state) => {
      state.enrichment = EMPTY_ENRICHMENT_STATS;
      state.enrichmentLoading = true;
    },
    bucketsLoaded: (state, action: PayloadAction<EnrichmentStats>) => {
      state.enrichment = action.payload;
      state.enrichmentLoading = false;
    },
    nlSearchStarted: (state) => {
      state.nlSearching = true;
    },
    nlSearchEnded: (state) => {
      state.nlSearching = false;
    },

    /** Reset everything to the initial state (on unmount). */
    resetForm: () => initialState,
  },
});

export const editListActions = slice.actions;
export const editListReducer = slice.reducer;
